import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import DocumentList from '../../components/Documents';
import DocumentFilters from '../../components/Documents/DocumentFilters';
import GroupDocsSection from '../../components/Documents/GroupDocsSection';
import UploadDocumentModal, { MY_DOCS_VALUE } from '../../components/Documents/UploadDocumentModal';
import "./style.less";
import UserContext from '../../../contexts/UserContext';
import { deleteDocumentFromGroup, fetchCommunityDocs, fetchMyDocs, fetchResidentGroupDocs, fetchDocumentFilterOptions } from '../../services/Documents';
import { Document } from '../../types/Document';
import { DocumentFilterOption, DocumentListFilters } from '../../types/DocumentFilters';
import { Group } from '../../types/Group';
import { useFacility } from '../../contexts/facility/FacilityContext';
import { fetchAllResidentGroups } from '../../services/Groups';
import sendToast from '../../../utils/Toast';
import settleAll from '../../../utils/settleAll';
import { EMPTY_DOCUMENT_FILTERS, mergeDocPage } from '../../utils/document';
import { isDefaultGroup } from '../../utils/group';
import { FileIcon, ChevronIcon, UsersIcon, GroupDocsIcon, ProfileIcon, UploadIcon } from '../../components/icons';

type DocsTab = "all" | "community" | "group" | "my";

const pageSize = 10;

const SECTION_TITLES: Record<DocsTab, { title: string; subtitle: string }> = {
    all: { title: 'All Documents', subtitle: 'Every document available to you, across all groups' },
    community: { title: 'Community Docs', subtitle: 'Documents shared with all residents' },
    group: { title: 'Group Docs', subtitle: 'Documents shared with your groups' },
    my: { title: 'My Docs', subtitle: 'Your personal documents on file' },
};

const Documents = () => {

  const [activeTab, setActiveTab] = useState<DocsTab>("all");
  const [groupSectionOpen, setGroupSectionOpen] = useState<boolean>(false);
  const [communityDocs, setCommunityDocs] = useState<Document[]>([]);
  const [groupDocs, setGroupDocs] = useState<Document[]>([]);
  const [myDocs, setMyDocs] = useState<Document[]>([]);
  // True until the first fetch finishes, so the empty state does not flash on open.
  const [communityDocsLoading, setCommunityDocsLoading] = useState<boolean>(true);
  const [groupDocsLoading, setGroupDocsLoading] = useState<boolean>(true);
  const [myDocsLoading, setMyDocsLoading] = useState<boolean>(true);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState<boolean>(false);
  const [communityDocsPageNo, setCommunityDocsPageNo] = useState<number>(1);
  const [groupDocsPageNo, setGroupDocsPageNo] = useState<number>(1);
  const [myDocsPageNo, setMyDocsPageNo] = useState<number>(1);
  const [communityDocsHasMore, setCommunityDocsHasMore] = useState<boolean>(true);
  const [groupDocsHasMore, setGroupDocsHasMore] = useState<boolean>(true);
  const [myDocsHasMore, setMyDocsHasMore] = useState<boolean>(true);
  const { facilityTimezone, loading: facilityLoading } = useFacility();
  const [filters, setFilters] = useState<DocumentListFilters>({ ...EMPTY_DOCUMENT_FILTERS });
  const [serviceOptions, setServiceOptions] = useState<DocumentFilterOption[]>([]);
  const [assetOptions, setAssetOptions] = useState<DocumentFilterOption[]>([]);
  // Every group the resident is a member of, regardless of whether it has a document yet —
  // unlike services/assets, groups aren't pared down to "has a document" (sidebar nav, the
  // "all" tab's per-group sections, and "Filter by Group" should always show all of them).
  const [groupOptions, setGroupOptions] = useState<DocumentFilterOption[]>([]);
  const [facilityGroups, setFacilityGroups] = useState<Group[]>([]);
  const [groupSectionLoading, setGroupSectionLoading] = useState<Record<string, boolean>>({});
  // Broadcasts the most recently deleted doc id to every mounted GroupDocsSection, since
  // each one owns its own independently-fetched local list.
  const [deletedDoc, setDeletedDoc] = useState<{ id: string } | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);

  const {
    userObject: {
      registrants: { list: listOfResidents, activeRegistrantsIndexes },
    },
  } = useContext(UserContext);

  const activeResident = listOfResidents[activeRegistrantsIndexes[0]]; // get the first resident from the list of active residents because neighbors web only supports the resident who is logged in and we can't deselect or change it

  // Bumped on every fetch per list; a response is only applied if it's still the latest
  // one requested for that list. Prevents an in-flight fetch for a stale page/filter combo
  // from resolving after — and overwriting — a newer, already-applied response.
  const communityRequestIdRef = useRef(0);
  const myRequestIdRef = useRef(0);
  const groupRequestIdRef = useRef(0);
  const filterOptionsRequestIdRef = useRef(0);

  // Loads service/asset/group filter options. `showLoading` is false for the
  // background refresh after an upload/delete (so a newly-attached/emptied
  // service or asset stops being stale) — that refresh must not re-trigger the
  // documents spinner (filterOptionsLoading feeds into `showAreaSpinner`).
  // Services/assets and groups are fetched via two separate settleAll([...]) calls
  // (each wrapping a single promise) rather than one settleAll([a, b]) call, so each
  // keeps its own distinct value type instead of collapsing to a union, and a
  // rejection from one doesn't discard a successful result from the other. Do not
  // "simplify" this to Promise.all([fetchA, fetchB]) — that reintroduces the old
  // all-or-nothing failure behavior.
  const loadFilterOptions = useCallback(async (showLoading: boolean = true) => {
    if (!activeResident?.Facility || !activeResident?._id) {
      return;
    }
    const requestId = ++filterOptionsRequestIdRef.current;
    try {
      if (showLoading) setFilterOptionsLoading(true);
      const [[optionsResult], [groupsResult]] = await Promise.all([
        settleAll([fetchDocumentFilterOptions(activeResident.Facility, String(activeResident._id))]),
        settleAll([fetchAllResidentGroups(activeResident.Facility, String(activeResident._id))]),
      ]);
      if (requestId !== filterOptionsRequestIdRef.current) return;

      if (optionsResult.status === 'fulfilled') {
        const { services, assets } = optionsResult.value;
        setServiceOptions(
          services
            .filter((service) => service?._id && service?.name)
            .map((service) => ({
              key: service._id,
              value: service._id,
              text: service.name as string,
            }))
            .sort((a, b) => a.text.localeCompare(b.text)),
        );
        setAssetOptions(
          assets
            .filter((asset) => asset?._id && asset?.name)
            .map((asset) => ({
              key: asset._id,
              value: asset._id,
              text: asset.name as string,
            }))
            .sort((a, b) => a.text.localeCompare(b.text)),
        );
      } else {
        sendToast('error', optionsResult.reason instanceof Error ? optionsResult.reason.message : 'Error loading service/asset filters');
      }

      if (groupsResult.status === 'fulfilled') {
        const groups = groupsResult.value;
        setGroupOptions(
          groups
            .filter((group) => group?._id && group?.Name)
            .map((group) => ({
              key: group._id,
              value: group._id,
              text: group.Name,
            }))
            .sort((a, b) => a.text.localeCompare(b.text)),
        );
        setFacilityGroups(groups);
      } else {
        sendToast('error', groupsResult.reason instanceof Error ? groupsResult.reason.message : 'Error loading groups');
      }
    } finally {
      // Always clear on the latest request, regardless of whether this particular
      // call was the one that set it — otherwise a showLoading:false call that
      // outlives/overlaps an earlier showLoading:true call never clears the spinner.
      if (requestId === filterOptionsRequestIdRef.current) setFilterOptionsLoading(false);
    }
  }, [activeResident?.Facility, activeResident?._id]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // If a service/asset the user has filtered by drops out of the options (e.g. its
  // last document was deleted), clear that filter so the doc-list effects (keyed on
  // `filters`) refetch instead of silently showing a stale, now-invalid selection.
  useEffect(() => {
    if (filters.serviceId && !serviceOptions.some((option) => option.value === filters.serviceId)) {
      handleFiltersChange({ ...filters, serviceId: '' });
      return;
    }
    if (filters.assetId && !assetOptions.some((option) => option.value === filters.assetId)) {
      handleFiltersChange({ ...filters, assetId: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceOptions, assetOptions]);

  // Split into one effect per doc source — each keyed only on its own page number — so
  // paging one list (e.g. Community's Load More, within the "All Documents" view) can't
  // also re-trigger a fetch for an unrelated one (e.g. My Docs) just because they shared
  // a single effect's dependency array.
  useEffect(() => {
    if (!facilityTimezone) return;
    if (activeTab === "all" || activeTab === "community") {
      listCommunityDocs();
    }
  }, [facilityTimezone, activeTab, communityDocsPageNo, filters]);

  useEffect(() => {
    if (!facilityTimezone) return;
    if (activeTab === "all" || activeTab === "my") {
      listMyDocs();
    }
  }, [facilityTimezone, activeTab, myDocsPageNo, filters]);

  useEffect(() => {
    if (!facilityTimezone) return;
    // Group docs on the "all" tab are fetched per-group by each GroupDocsSection below instead.
    if (activeTab === "group") {
      listGroupDocs();
    }
  }, [facilityTimezone, activeTab, groupDocsPageNo, filters]);

  // Each list function accepts an optional page-number override so callers that need an
  // immediate, guaranteed page-1 refetch (delete/create) don't have to wait a render for
  // state to settle before reading it back out of the closure.
  const listCommunityDocs = async (pageNoOverride?: number) => {
    const pageNo = pageNoOverride ?? communityDocsPageNo;
    const requestId = ++communityRequestIdRef.current;
    try {
      setCommunityDocsLoading(true);
      const response = await fetchCommunityDocs(
        activeResident.Facility,
        pageNo,
        pageSize,
        filters,
      );
      if (requestId !== communityRequestIdRef.current) return;
      setCommunityDocsHasMore(response.Result.Content.TotRecords > pageNo * pageSize);
      setCommunityDocs((prev) => mergeDocPage(prev, pageNo, response.Result.Content.Result));
    } catch (error) {
      if (requestId !== communityRequestIdRef.current) return;
      sendToast('error', error);
    } finally {
      if (requestId === communityRequestIdRef.current) setCommunityDocsLoading(false);
    }
  };
  const listMyDocs = async (pageNoOverride?: number) => {
    const pageNo = pageNoOverride ?? myDocsPageNo;
    const requestId = ++myRequestIdRef.current;
    try {
      setMyDocsLoading(true);
      const response = await fetchMyDocs(
        activeResident.Facility,
        String(activeResident._id),
        pageNo,
        pageSize,
        filters,
      );
      if (requestId !== myRequestIdRef.current) return;
      setMyDocsHasMore(response.Result.Content.TotRecords > pageNo * pageSize);
      setMyDocs((prev) => mergeDocPage(prev, pageNo, response.Result.Content.Result));
    } catch (error) {
      if (requestId !== myRequestIdRef.current) return;
      sendToast('error', error);
    } finally {
      if (requestId === myRequestIdRef.current) setMyDocsLoading(false);
    }
  };

  const listGroupDocs = async (pageNoOverride?: number) => {
    const pageNo = pageNoOverride ?? groupDocsPageNo;
    const requestId = ++groupRequestIdRef.current;
    try {
      setGroupDocsLoading(true);
      const response = await fetchResidentGroupDocs(
        activeResident.Facility,
        String(activeResident._id),
        pageNo,
        pageSize,
        filters,
      );
      if (requestId !== groupRequestIdRef.current) return;
      setGroupDocsHasMore(response.Result.Content.TotRecords > pageNo * pageSize);
      setGroupDocs((prev) => mergeDocPage(prev, pageNo, response.Result.Content.Result));
    } catch (error) {
      if (requestId !== groupRequestIdRef.current) return;
      sendToast('error', error);
    } finally {
      if (requestId === groupRequestIdRef.current) setGroupDocsLoading(false);
    }
  };

  // Deletes a doc everywhere it's visible on this page in one go. A doc can be shown in
  // more than one bucket at once (e.g. "My Docs" and a group's section both show it), so
  // a delete from any one list has to clear it from all of them — including every
  // currently-mounted GroupDocsSection, which each keep their own independently-fetched
  // local state and wouldn't otherwise learn about a delete triggered elsewhere. We signal
  // those via `deletedDoc`, bumped on every successful delete regardless of origin.
  const deleteDocument = async (doc: Document): Promise<boolean> => {
    try {
      if (!doc.UploadedByResidentId || doc.UploadedByResidentId !== String(activeResident._id)) {
        sendToast('error', 'You are not authorized to delete this document');
        return false;
      }
      setDeleteLoading(true);
      await deleteDocumentFromGroup(activeResident.Facility, doc._id, doc.UploadedByResidentId as string);
      sendToast('success', 'Document deleted successfully');
      setCommunityDocs((prev) => prev.filter((d) => d._id !== doc._id));
      setGroupDocs((prev) => prev.filter((d) => d._id !== doc._id));
      setMyDocs((prev) => prev.filter((d) => d._id !== doc._id));
      setDeletedDoc({ id: doc._id });
      // Deleting a doc may have removed the last one attached to a service/asset —
      // refresh those filter options in the background (no documents spinner).
      loadFilterOptions(false);
      return true;
    } catch (error) {
      sendToast('error', error instanceof Error ? error.message : 'Error deleting document');
      return false;
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteDocument = deleteDocument;

  const handleGroupSectionLoadingChange = (groupId: string, loading: boolean) => {
    setGroupSectionLoading((prev) => (prev[groupId] === loading ? prev : { ...prev, [groupId]: loading }));
  };

  // Shared by tab switches and filter changes — both need to reset pagination for
  // whichever tab(s) are affected.
  const resetPaginationForTab = (tab: DocsTab) => {
    if (tab === "all") {
      // Each GroupDocsSection resets its own pagination when these filters change.
      setCommunityDocsHasMore(true);
      setCommunityDocsPageNo(1);
      setMyDocsHasMore(true);
      setMyDocsPageNo(1);
      return;
    }
    if (tab === "community") {
      setCommunityDocsHasMore(true);
      setCommunityDocsPageNo(1);
      return;
    }
    if (tab === "my") {
      setMyDocsHasMore(true);
      setMyDocsPageNo(1);
      return;
    }
    setGroupDocsHasMore(true);
    setGroupDocsPageNo(1);
  };

  const handleFiltersChange = (nextFilters: DocumentListFilters) => {
    setFilters(nextFilters);
    resetPaginationForTab(activeTab);
  };

  const handleTabChange = (tab: DocsTab) => {
    setActiveTab(tab);
    setFilters({ ...EMPTY_DOCUMENT_FILTERS });
    resetPaginationForTab(tab);
  };

  const handleGroupNavClick = () => {
    setGroupSectionOpen((prev) => !prev);
  };

  const handleGroupChildClick = (groupId: string) => {
    setGroupSectionOpen(true);
    setActiveTab("group");
    setFilters({ ...EMPTY_DOCUMENT_FILTERS, groupId });
    resetPaginationForTab("group");
  };

  // Default/system-managed groups (e.g. the facility-wide Community group) are excluded
  // here on purpose — residents can't manually upload into them, unlike the sidebar/filter
  // group list above which intentionally still shows every group the resident belongs to.
  const uploadGroupOptions = facilityGroups
    .filter((group) => group?._id && group?.Name && !isDefaultGroup(group))
    .map((group) => ({ key: group._id, value: group._id, text: group.Name }))
    .sort((a, b) => a.text.localeCompare(b.text));

  // Jump the resident to wherever their new document landed, mirroring the upload flow's
  // "show me what I just uploaded" behavior.
  const handleUploadSuccess = (targetGroupId: string) => {
    if (targetGroupId === MY_DOCS_VALUE) {
      setMyDocsHasMore(true);
      if (activeTab === 'my') {
        if (myDocsPageNo === 1) listMyDocs(1);
        else setMyDocsPageNo(1);
      } else {
        // Switching tabs, so reset filters same as handleTabChange — otherwise whatever
        // search/service/asset filters were active on the previous tab stay selected
        // (and visibly so, in the filter bar) after landing on My Docs.
        setFilters({ ...EMPTY_DOCUMENT_FILTERS });
        setMyDocsPageNo(1);
        setActiveTab('my');
      }
      return;
    }
    handleGroupChildClick(targetGroupId);
  };

  const info = SECTION_TITLES[activeTab];

  const canFetch = !!facilityTimezone;

  const singleActiveDocs = activeTab === "community" ? communityDocs : activeTab === "group" ? groupDocs : myDocs;
  const singleActiveLoading = activeTab === "community"
    ? communityDocsLoading
    : activeTab === "group"
      ? groupDocsLoading
      : myDocsLoading;
  const singleActivePageNo = activeTab === "community"
    ? communityDocsPageNo
    : activeTab === "group"
      ? groupDocsPageNo
      : myDocsPageNo;
  const singleActiveHasMore = activeTab === "community" ? communityDocsHasMore : activeTab === "group" ? groupDocsHasMore : myDocsHasMore;
  const singleActiveSetPageNo = activeTab === "community" ? setCommunityDocsPageNo : activeTab === "group" ? setGroupDocsPageNo : setMyDocsPageNo;

  // Every doc list's `loading` also factors in these two global states.
  // List flags stay true for paging too, so Load More can spin on its own button,
  // but they must not count when the fetch effects bailed out (no timezone).
  const globalBusy = facilityLoading || deleteLoading;
  const communityListLoading = canFetch && communityDocsLoading;
  const myListLoading = canFetch && myDocsLoading;
  const singleListLoading = canFetch && singleActiveLoading;

  const anyGroupSectionLoading = Object.values(groupSectionLoading).some(Boolean);

  // Hide the documents area only for a fresh load (page 1). Paging keeps the
  // lists mounted and visible so scroll position and keyboard focus stay put.
  // Group sections report loading only for page 1 (see GroupDocsSection).
  const communityBlocking = communityDocsLoading && communityDocsPageNo === 1;
  const myBlocking = myDocsLoading && myDocsPageNo === 1;
  const singleBlocking = singleActiveLoading && singleActivePageNo === 1;
  const groupsPending = activeTab === "all" && groupOptions.length > 0 && (
    anyGroupSectionLoading || Object.keys(groupSectionLoading).length < groupOptions.length
  );
  const showAreaSpinner = facilityLoading || (canFetch && (
    activeTab === "all"
      ? filterOptionsLoading || communityBlocking || myBlocking || groupsPending
      : singleBlocking
  ));

  return (
    <div className="documentsPage w-full min-w-0">
      <aside className="documentsSidebar min-w-0">
        <div className="sidebar-section">
          <button type="button" className={`sidebar-btn ${activeTab === "all" ? "active" : ""}`} onClick={() => handleTabChange("all")}>
            <div className="sidebar-btn-left"><FileIcon /><span className="sidebar-btn-label">All Documents</span></div>
          </button>
        </div>
        <div className="sidebar-section">
          <button type="button" className={`sidebar-btn ${activeTab === "community" ? "active" : ""}`} onClick={() => handleTabChange("community")}>
            <div className="sidebar-btn-left"><UsersIcon /><span className="sidebar-btn-label">Community Docs</span></div>
          </button>
        </div>
        <div className="sidebar-section">
          <button type="button" className="sidebar-btn" onClick={handleGroupNavClick}>
            <div className="sidebar-btn-left"><GroupDocsIcon /><span className="sidebar-btn-label">Group Docs</span></div>
            <span className={`chevron ${groupSectionOpen ? "open" : ""}`}><ChevronIcon /></span>
          </button>
          {groupSectionOpen && (
            <div className="sidebar-children">
              {groupOptions.length > 0 ? groupOptions.map((group) => (
                <button
                  type="button"
                  key={group.key}
                  className={`sidebar-child break-words ${activeTab === "group" && filters.groupId === group.value ? "active" : ""}`}
                  onClick={() => handleGroupChildClick(group.value)}
                >
                  {group.text}
                </button>
              )) : (
                <div className="sidebar-child-empty">{filterOptionsLoading ? "Loading groups…" : "No groups yet"}</div>
              )}
            </div>
          )}
        </div>
        <div className="sidebar-section">
          <button type="button" className={`sidebar-btn ${activeTab === "my" ? "active" : ""}`} onClick={() => handleTabChange("my")}>
            <div className="sidebar-btn-left"><ProfileIcon /><span className="sidebar-btn-label">My Docs</span></div>
          </button>
        </div>
      </aside>

      <div className="documentsMain min-w-0 w-full">
        <div className="documentsPageHeader flex-wrap min-w-0">
          <div className="documentsPageTitleGroup min-w-0">
            <div className="documentsPageTitle break-words">{info.title}</div>
            <div className="documentsPageSubtitle break-words">{info.subtitle}</div>
          </div>
          <button type="button" className="documentsUploadBtn" onClick={() => setUploadModalOpen(true)}>
            <UploadIcon size={18} />
            Upload Document
          </button>
        </div>

        <DocumentFilters
          filters={filters}
          serviceOptions={serviceOptions}
          assetOptions={assetOptions}
          groupOptions={groupOptions}
          loadingOptions={filterOptionsLoading}
          hideGroupFilter={activeTab === "group"}
          onFiltersChange={handleFiltersChange}
        />

        <div className={`documentsContent${showAreaSpinner ? " is-loading" : ""}`}>
          {showAreaSpinner && (
            <div className="documentsAreaSpinner" role="status" aria-label="Loading documents" />
          )}
          <div hidden={showAreaSpinner}>
          {activeTab === "all" ? (
            <>
              <div className="docs-section">
                <div className="docs-section-label">Community</div>
                <DocumentList docs={communityDocs} loading={globalBusy || communityListLoading} hasMore={communityDocsHasMore} setPageNo={setCommunityDocsPageNo} facilityTimezone={facilityTimezone} activeResidentId={activeResident._id} onDelete={handleDeleteDocument} />
              </div>
              {groupOptions
                .filter((group) => !filters.groupId || filters.groupId === group.value)
                .map((group) => (
                <GroupDocsSection
                  key={group.key}
                  facilityId={activeResident.Facility}
                  residentId={String(activeResident._id)}
                  groupId={group.value}
                  groupLabel={group.text}
                  filters={filters}
                  pageSize={pageSize}
                  facilityTimezone={facilityTimezone}
                  activeResidentId={activeResident._id}
                  extraLoading={globalBusy}
                  onDelete={deleteDocument}
                  deletedDoc={deletedDoc}
                  onLoadingChange={handleGroupSectionLoadingChange}
                />
              ))}
              <div className="docs-section">
                <div className="docs-section-label">My Docs</div>
                <DocumentList docs={myDocs} loading={globalBusy || myListLoading} hasMore={myDocsHasMore} setPageNo={setMyDocsPageNo} facilityTimezone={facilityTimezone} activeResidentId={activeResident._id} onDelete={handleDeleteDocument} />
              </div>
            </>
          ) : (
            <DocumentList
              docs={singleActiveDocs}
              loading={globalBusy || singleListLoading}
              hasMore={singleActiveHasMore}
              setPageNo={singleActiveSetPageNo}
              facilityTimezone={facilityTimezone}
              activeResidentId={activeResident._id}
              onDelete={handleDeleteDocument}
            />
          )}
          </div>
        </div>
      </div>

      <UploadDocumentModal
        open={uploadModalOpen}
        facilityId={activeResident.Facility}
        residentId={String(activeResident._id)}
        groupOptions={uploadGroupOptions}
        onClose={() => setUploadModalOpen(false)}
        onUploaded={handleUploadSuccess}
      />
    </div>
  );
};

export default Documents;
